import { FloatingPortal } from "@floating-ui/react";
import { useCallback, useEffect, useId, useState } from "react";
import { useMediaQuery } from "../../hooks";
import { PanelContent } from "./PanelContent";
import { PanelContext } from "./context";
import type { PanelProps } from "./types";

export const Panel = (props: PanelProps) => {
  const { open, onOpenChange } = props;
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);
  const titleId = useId();
  const descriptionId = useId();
  const isMobile = useMediaQuery("(max-width: 639px)");

  const defaultWidth = props.defaultWidth ?? 400;
  const [internalWidth, setInternalWidth] = useState(
    props.width ?? defaultWidth,
  );
  const currentWidth = props.width ?? internalWidth;

  const handleResize = useCallback(
    (width: number) => {
      if (props.width === undefined) {
        setInternalWidth(width);
      }
      props.onWidthChange?.(width);
    },
    [props.width, props.onWidthChange],
  );

  const handleDismiss = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAnimationEnd = useCallback(() => {
    if (!open) {
      setMounted(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const rafId = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(rafId);
    } else {
      setVisible(false);
    }
  }, [open]);

  if (!mounted && !open) {
    return null;
  }

  const contextValue = {
    ...props,
    handleDismiss,
    handleAnimationEnd,
    mounted,
    visible,
    titleId,
    descriptionId,
    currentWidth,
    handleResize,
    isMobile,
  };

  return (
    <PanelContext.Provider value={contextValue}>
      <FloatingPortal>{mounted && <PanelContent />}</FloatingPortal>
    </PanelContext.Provider>
  );
};
